import React, { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import usePulseLocale from "./i18n";
import { tokens, fmtCompact, fmtPct, fmtPeriod, MONTH_LABELS } from "./theme";
import {
  SkeletonCard, NoData, SourceChip, SectionTitle, SrTable, ExperimentalBadge, ChartTooltipContent,
} from "./components";
import { pulseGet } from "./api";
import { Info } from "lucide-react";

/** Seasonality & Platforms — the differentiator screen (tab of Country Pulse,
 *  deep-linked as ?view=seasonality). */
export default function SeasonalityView({ iso3, label, dm }) {
  const { t, lang } = usePulseLocale();
  const T = tokens(dm);
  const [monthly, setMonthly] = useState(null);
  const [platforms, setPlatforms] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true;
    setLoading(true); setMonthly(null); setPlatforms(null);
    Promise.allSettled([
      pulseGet(`monthly/${iso3}`, { months: 72 }),
      pulseGet(`platforms/${iso3}`),
    ]).then(([m, p]) => {
      if (!on) return;
      if (m.status === "fulfilled") setMonthly(m.value);
      if (p.status === "fulfilled") setPlatforms(p.value);
      setLoading(false);
    });
    return () => { on = false; };
  }, [iso3]);

  // Seasonality: average monthly distribution over the last 3 FULL years vs current year
  const seas = useMemo(() => {
    const pts = monthly?.series?.nights_total || [];
    if (!pts.length) return null;
    const byYear = {};
    pts.forEach((p) => {
      const [y, m] = p.period.split("-");
      (byYear[y] = byYear[y] || {})[parseInt(m, 10)] = p.value;
    });
    const years = Object.keys(byYear).sort();
    const fullYears = years.filter((y) => Object.keys(byYear[y]).length === 12);
    const last3 = fullYears.slice(-3);
    if (!last3.length) return null;
    const curYear = years[years.length - 1];
    const rows = [];
    for (let m = 1; m <= 12; m++) {
      const vals = last3.map((y) => byYear[y][m]).filter((v) => v !== undefined);
      rows.push({
        month: MONTH_LABELS[lang][m - 1],
        avg: vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
        cur: curYear !== last3[last3.length - 1] ? byYear[curYear]?.[m] ?? null : null,
      });
    }
    return { rows, from: last3[0], to: last3[last3.length - 1], curYear };
  }, [monthly, lang]);

  const platRows = useMemo(() => {
    const pts = platforms?.platform_nights || [];
    return pts.slice(-36).map((p) => ({ period: fmtPeriod(p.period, lang), value: p.value, yoy: p.yoy_pct }));
  }, [platforms, lang]);

  const intRows = useMemo(() => {
    const pts = platforms?.platform_intensity || [];
    return pts.map((p) => ({ period: p.period, value: p.value }));
  }, [platforms]);

  const si = platforms?.seasonality_index;
  const lastMonthly = (monthly?.series?.nights_total || []).slice(-1)[0]?.period;
  const lastPlat = (platforms?.platform_nights || []).slice(-1)[0]?.period;

  return (
    <div data-testid="seasonality-view">
      {/* Seasonality curve */}
      <section className="mb-8">
        <SectionTitle dm={dm} right={si && (
          <span className="text-[11px] inline-flex items-center gap-1 px-2 py-1 cursor-help" data-testid="seasonality-index-chip"
            title={t("seas_index_tip", { period: si.period })}
            style={{ background: T.brandSoft, color: T.accent, borderRadius: 2 }}>
            {t("seas_index")}: <strong className="pulse-num">{fmtPct(si.value, lang, false)}</strong>
            <Info className="w-3 h-3" aria-hidden="true" />
          </span>
        )}>
          {t("seas_title")}
        </SectionTitle>
        {loading && <SkeletonCard h={260} dm={dm} />}
        {!loading && !seas && <NoData dm={dm} h={160} msg={t("no_monthly", { country: label })} testid="seasonality-no-data" />}
        {!loading && seas && (
          <div className="p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="seasonality-chart">
            <div style={{ height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={seas.rows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                  <CartesianGrid stroke={T.border} vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={{ stroke: T.border }} />
                  <YAxis tickFormatter={(v) => fmtCompact(v, lang)} tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} />
                  <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="avg" name={t("seas_avg", { from: seas.from, to: seas.to })}
                    stroke={dm ? "#9CA3AF" : "#6B7280"} strokeDasharray="5 4" strokeWidth={1.75} dot={false} isAnimationActive={true} animationDuration={400} />
                  <Line type="monotone" dataKey="cur" name={t("seas_current", { year: seas.curYear })}
                    stroke={T.brand} strokeWidth={2.25} dot={false} connectNulls isAnimationActive={true} animationDuration={400} />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2"><SourceChip source="EUROSTAT" dataset="tour_occ_nim" period={lastMonthly} dm={dm} /></div>
            <SrTable caption={t("seas_title")}
              columns={["Month", t("seas_avg", { from: seas.from, to: seas.to }), t("seas_current", { year: seas.curYear })]}
              rows={seas.rows.map((r) => [r.month, fmtCompact(r.avg, lang), fmtCompact(r.cur, lang)])} />
          </div>
        )}
      </section>

      {/* Platform economy panel */}
      <section className="mb-4">
        <SectionTitle dm={dm} right={<ExperimentalBadge dm={dm} />}>{t("plat_title")}</SectionTitle>
        {loading && <SkeletonCard h={260} dm={dm} />}
        {!loading && !platRows.length && <NoData dm={dm} h={160} msg={t("no_platform", { country: label })} testid="platforms-no-data" />}
        {!loading && platRows.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 p-4" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="platform-nights-chart">
              <div style={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={platRows} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                    <CartesianGrid stroke={T.border} vertical={false} />
                    <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.muted }} interval="preserveStartEnd" tickLine={false} axisLine={{ stroke: T.border }} />
                    <YAxis tickFormatter={(v) => fmtCompact(v, lang)} tick={{ fontSize: 10, fill: T.muted }} tickLine={false} axisLine={false} width={48} />
                    <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtCompact(v, lang)} />} />
                    <Bar dataKey="value" name={t("ind_platform_nights")} fill="#F59E0B" isAnimationActive={true} animationDuration={400} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="mt-2"><SourceChip source="EUROSTAT" dataset="tour_ce_omr" period={lastPlat} experimental dm={dm} /></div>
              <SrTable caption={t("plat_title")} columns={[t("rank_col_year"), t("ind_platform_nights"), t("yoy")]}
                rows={platRows.map((r) => [r.period, fmtCompact(r.value, lang), fmtPct(r.yoy, lang)])} />
            </div>
            <div className="p-4 flex flex-col" style={{ background: T.surface, border: `1px solid ${T.border}` }} data-testid="platform-intensity-panel">
              <p className="text-[11px] font-semibold uppercase tracking-wider mb-1 inline-flex items-center gap-1" style={{ color: T.muted }}>
                {t("plat_intensity")}
                <Info className="w-3 h-3 cursor-help" aria-label={t("plat_intensity_tip")} title={t("plat_intensity_tip")} />
              </p>
              {intRows.length > 0 ? (
                <>
                  <p className="pulse-num text-3xl font-semibold mb-2">
                    {fmtPct(intRows[intRows.length - 1].value, lang, false)}
                    <span className="text-xs font-normal ml-2" style={{ color: T.muted }}>{intRows[intRows.length - 1].period}</span>
                  </p>
                  <div style={{ height: 120 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={intRows} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                        <XAxis dataKey="period" tick={{ fontSize: 9, fill: T.muted }} tickLine={false} axisLine={{ stroke: T.border }} />
                        <YAxis hide />
                        <Tooltip content={<ChartTooltipContent dm={dm} formatter={(v) => fmtPct(v, lang, false)} />} />
                        <Line type="monotone" dataKey="value" name={t("plat_intensity")} stroke="#F59E0B" strokeWidth={2} dot={false} isAnimationActive={true} animationDuration={400} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <SrTable caption={t("plat_intensity")} columns={[t("rank_col_year"), t("plat_intensity")]}
                    rows={intRows.map((r) => [r.period, fmtPct(r.value, lang, false)])} />
                </>
              ) : (
                <NoData dm={dm} h={120} />
              )}
              <p className="text-[10px] leading-relaxed mt-3" style={{ color: T.muted }}>{t("plat_intensity_tip")}</p>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
